/**
 * The client's master account «Общий баланс» (docs/cabinet-redesign/04_МАСТЕР_СЧЁТ_АРХИТЕКТУРА.md §7, §8).
 * Customer-JWT zone — /api/v1/showcase/me/master-account, answered by ShowcaseMasterAccountController
 * (MasterAccountService, MasterToCardService). Off for the client → 404 MASTER_ACCOUNT_OFF; the cabinet asks only when
 * `capabilities.masterAccount` is true.
 *
 * Amounts are Java BigDecimal in the coin's major units: Jackson writes them as JSON numbers; a string is tolerated
 * so a server-side switch to string decimals does not break the screens (`@/utils/money` formats both).
 */
import { http } from './http';

const BASE = '/v1/showcase/me/master-account';

/** A Java BigDecimal on the wire. */
export type Decimal = number | string;

/** A coin × network pair the rail offers (shared AssetNetwork record), upper-case. */
export interface MasterPair {
  asset: string;
  network: string;
}

/** Custody of the address key: ours until the Privy variant (stage M6). */
export type MasterCustody = 'PLATFORM' | 'PRIVY';

/** One permanent master address (MasterAccountService.AddressView). */
export interface MasterAddress {
  id: string;
  network: string;
  asset: string;
  address: string;
  custody: MasterCustody;
  available: Decimal;
  onReview: Decimal;
  inTransit: Decimal;
  frozen: Decimal;
  createdAt: string;
  /**
   * What arrived and waits under the rail's floor (BELOW_FLOOR deferral), coin major units; null while nothing does —
   * the screen names it only then. Absent on an older server.
   */
  belowFloor?: Decimal | null;
}

/** Where an arrival's money is (MasterBalance.Bucket) — never a raw screening verdict. */
export type ArrivalState = 'AVAILABLE' | 'ON_REVIEW' | 'FROZEN' | 'RETURNED';

/** Where a «to card» request is (MasterAccountService.ToCardState). */
export type ToCardState = 'QUEUED' | 'WAITING_CARD' | 'SENDING' | 'TO_CARD' | 'CREDITED' | 'CANCELLED' | 'RETURNED';

interface MovementBase {
  id: string;
  network: string;
  asset: string;
  amount: Decimal;
  /** The arrival's hash, or the hop's once signed; null before. */
  txHash: string | null;
  at: string | null;
}

export interface ArrivalMovement extends MovementBase {
  kind: 'ARRIVAL';
  state: ArrivalState;
  cardId: null;
}

export interface ToCardMovement extends MovementBase {
  kind: 'TO_CARD';
  state: ToCardState;
  /** Null while the client has no card — the rail reserves a pool card when the money moves. */
  cardId: string | null;
}

/** One movement of the account (MasterAccountService.MovementView), arrivals and «to card» in one feed. */
export type MasterMovement = ArrivalMovement | ToCardMovement;

/** GET / (MasterAccountService.MasterAccountView): totals add the stablecoins up 1:1 (USD). */
export interface MasterAccountView {
  available: Decimal;
  onReview: Decimal;
  inTransit: Decimal;
  frozen: Decimal;
  addresses: MasterAddress[];
  /** The pairs the client may still open an address on (the rail's offer minus what is open). */
  openable: MasterPair[];
  /** The latest movements, newest first (at most 20). */
  recentMovements: MasterMovement[];
  /**
   * The smallest «to card» amount, USD — the same in every coin the rail takes (USD stablecoins); the server refuses
   * less with MASTER_AMOUNT_BELOW_MIN. Absent on an older server.
   */
  toCardMinimum?: Decimal | null;
}

/** GET /to-card/estimate (MasterToCardService.EstimateView). */
export interface ToCardEstimate {
  network: string;
  asset: string;
  cardId: string | null;
  /** What leaves the master balance. */
  amount: Decimal;
  /** What the card will be credited — null while `pending`. */
  toCard: Decimal | null;
  /** The part that pays for the card (only while the issuance is unpaid), else null. */
  cardPrice: Decimal | null;
  /** The top-up fee — null while `pending`. */
  fee: Decimal | null;
  /** The client's whole top-up percent — null while `pending`. */
  percent: Decimal | null;
  /** The tariff or the provider's fees are not determined yet: only `amount` is known, the rest is computed on arrival. */
  pending: boolean;
  /** Available on this master address now. */
  available: Decimal;
  /** The balance covers the amount. */
  sufficient: boolean;
  /** The rail's smallest top-up in the coin's major units (null — no floor reported). */
  minimum?: Decimal | null;
  /** The group member whose card it is (docs/cabinet-redesign/07); null for one's own card. */
  recipientId?: string | null;
}

/**
 * POST /to-card body. `cardId` absent — the rail's top-up card of the client (or of the recipient). `recipientId` — a
 * member of the client's groups whose card is topped up (docs/cabinet-redesign/07 §4.6); absent for one's own card.
 */
export interface ToCardCommand {
  cardId?: string | null;
  network: string;
  asset: string;
  /** Plain decimal string in the coin's major units («25.5»). */
  amount: string;
  recipientId?: string | null;
}

/**
 * The rail request's status (TopUpStatus name): a fresh command starts APPROVED, or SUBMITTED while an operator
 * reviews it; a replayed one may already be further on.
 */
export type ToCardRequestStatus = string;

/** POST /to-card answer (MasterToCardService.ToCardResult). */
export interface ToCardResult {
  requestId: string;
  status: ToCardRequestStatus;
  masterWalletId: string;
  network: string;
  asset: string;
  amount: Decimal;
  cardId: string | null;
  /** What is left on this master address after the command. */
  available: Decimal;
  /** The same Idempotency-Key was seen before — this is that request again. */
  replayed: boolean;
  /** The member whose card it is; null for one's own. */
  recipientId?: string | null;
}

/** One card of a group member (GroupMemberCards.CardOption). */
export interface MemberCardOption {
  cardId: string;
  displayName: string | null;
  lastFour: string | null;
  /** CardStatus name (ACTIVE, FROZEN, REQUESTED, BLOCKED…). */
  status: string;
  /** Not blocked — a REQUESTED card can: the gift pays its card price first. */
  canReceive: boolean;
  /** The card a top-up goes to when none is chosen. */
  topUpCard: boolean;
}

/** Group circle as the core names it; null — not marked yet. */
export type MemberCircle = 'FAMILY' | 'HOUSEHOLD' | 'BUSINESS';

/** GET /recipients (GroupMemberCards.RecipientView): a member whose card the client may top up. */
export interface MemberRecipient {
  recipientId: string;
  name: string | null;
  circle: MemberCircle | null;
  /** The operator's label («Повар»), null when none. */
  roleLabel: string | null;
  /** PRINCIPAL / MEMBER. */
  role: string;
  cards: MemberCardOption[];
  /** At least one card can receive. */
  canReceive: boolean;
}

/** Where a member's card top-up is (MemberTopUpHistory.State). */
export type MemberTopUpState = 'IN_PROGRESS' | 'CREDITED' | 'CANCELLED' | 'RETURNED';

/** GET /member-top-ups (MemberTopUpHistory.MemberTopUpView): both sides of the members' card top-ups. */
export interface MemberTopUp {
  requestId: string;
  /** OUTGOING — I sent it from my shared balance; INCOMING — a member topped up my card. */
  direction: 'OUTGOING' | 'INCOMING';
  /** The recipient for OUTGOING, the sender for INCOMING. */
  counterpartId: string;
  counterpartName: string | null;
  cardId: string | null;
  cardLastFour: string | null;
  cardDisplayName: string | null;
  network: string;
  asset: string;
  /** What left the sender's shared balance. */
  amount: Decimal | null;
  /** What the provider put on the card, once known. */
  credited: Decimal | null;
  state: MemberTopUpState;
  /** The money request it answered, if any. */
  moneyRequestId: string | null;
  createdAt: string | null;
  /**
   * Why a CANCELLED one was cancelled — a code (RECIPIENT_LEFT_GROUP: a member left the family before the money left,
   * TO_CARD_WAITING_TIMEOUT, TO_CARD_CANCELLED_BY_SUPPORT); null otherwise. Absent on an older server.
   */
  cancelReason?: string | null;
}

/** The error codes these endpoints answer with (all translated under vitrine.error.*). */
export type MasterErrorCode =
  | 'MASTER_ACCOUNT_OFF'
  | 'IDEMPOTENCY_KEY_REQUIRED'
  | 'IDEMPOTENCY_KEY_REUSED'
  | 'MASTER_AMOUNT_INVALID'
  | 'MASTER_ADDRESS_NOT_OPEN'
  | 'CARD_NOT_OWNED'
  | 'TOPUP_CARD_CANNOT_RECEIVE'
  | 'TOPUP_PAIR_PROVIDER_UNSUPPORTED'
  | 'TOPUP_NETWORK_UNSUPPORTED'
  | 'TOPUP_DEPOSIT_RAIL_UNAVAILABLE'
  | 'MASTER_INSUFFICIENT'
  | 'TOPUP_ISSUANCE_ALREADY_PAID'
  | 'MASTER_AMOUNT_BELOW_MIN'
  | 'MASTER_TO_CARD_LIMIT'
  | 'MASTER_TO_CARD_RATE_LIMITED'
  | 'MASTER_WALLET_NOT_FOUND'
  | 'MASTER_KEY_EXPORT_RATE_LIMITED'
  | 'MASTER_KEY_EXPORT_PAUSED_EMAIL_CHANGED'
  | 'MASTER_KEY_UNAVAILABLE'
  | 'GROUP_SEND_NOT_ALLOWED'
  | 'RECIPIENT_NO_CARD'
  | 'STEP_UP_REQUIRED';

/**
 * How the exported key is written — the form the network's common wallet imports:
 * TRON_HEX — hex of the 32-byte private key; SOLANA_BASE58 — base58 of the 64-byte secret (Phantom / Solflare);
 * TON_MNEMONIC — the 24 words (Tonkeeper); TON_SEED_HEX — hex of the seed, for a TON wallet that has no words;
 * ETHEREUM_HEX — hex of the 32-byte secp256k1 private key, no 0x prefix (MetaMask, Rabby: «import private key»).
 */
export type KeyExportFormat = 'TRON_HEX' | 'SOLANA_BASE58' | 'TON_MNEMONIC' | 'TON_SEED_HEX' | 'ETHEREUM_HEX';

/** POST /{walletId}/key-export (ShowcaseMasterAccountController.KeyExportView). `secret` is the key itself. */
export interface MasterKeyExport {
  walletId: string;
  network: string;
  asset: string;
  /** The address the key opens — for the client to check the import against. */
  address: string;
  /** One of {@link KeyExportFormat}, or a format a newer core added. */
  format: string;
  secret: string;
}

/**
 * The refusal body of these endpoints (ShowcaseMasterAccountController.ErrorView): the code, and for
 * MASTER_AMOUNT_BELOW_MIN the minimum in the coin's major units and its coin.
 */
export interface MasterErrorBody {
  /** One of {@link MasterErrorCode}, or a code a newer core added. */
  error: string;
  minimum?: Decimal;
  asset?: string;
}

export const masterAccountApi = {
  async view(): Promise<MasterAccountView> {
    return (await http.get<MasterAccountView>(BASE)).data;
  },

  /** Open the permanent address on a pair — idempotent: the live one comes back when it exists. */
  async openAddress(pair: MasterPair): Promise<MasterAddress> {
    return (await http.post<MasterAddress>(`${BASE}/addresses`, { network: pair.network, asset: pair.asset })).data;
  },

  /** «Придёт на карту N» for this amount now. */
  async estimate(params: { cardId?: string | null; recipientId?: string | null; network: string; asset: string;
                          amount: string },
                 signal?: AbortSignal): Promise<ToCardEstimate> {
    const query: Record<string, string> = { network: params.network, asset: params.asset, amount: params.amount };
    if (params.cardId) query.cardId = params.cardId;
    if (params.recipientId) query.recipientId = params.recipientId;
    return (await http.get<ToCardEstimate>(`${BASE}/to-card/estimate`, { params: query, signal })).data;
  },

  /**
   * «На карту». `idempotencyKey` is one tap's key: a fresh UUID per tap, the same one on a retry of that tap
   * (the server then answers with the same request, `replayed: true`). A member's card (`recipientId`) needs a step-up:
   * without `grant` the server answers 403 STEP_UP_REQUIRED with the factors; call again with the grant of purpose
   * MEMBER_TOP_UP and the SAME key.
   */
  async toCard(cmd: ToCardCommand, idempotencyKey: string, grant?: string): Promise<ToCardResult> {
    const body = { cardId: cmd.cardId ?? null, network: cmd.network, asset: cmd.asset, amount: cmd.amount,
      recipientId: cmd.recipientId ?? null };
    const headers: Record<string, string> = { 'Idempotency-Key': idempotencyKey };
    if (grant) headers['X-Reveal-Grant'] = grant;
    return (await http.post<ToCardResult>(`${BASE}/to-card`, body, { headers })).data;
  },

  /** «Пополнить карту близкого» — the members of my groups whose cards I may top up from my shared balance. */
  async recipients(): Promise<MemberRecipient[]> {
    return (await http.get<MemberRecipient[]>(`${BASE}/recipients`)).data;
  },

  /** Both sides of the members' card top-ups, newest first. Open without one's own shared balance. */
  async memberTopUps(): Promise<MemberTopUp[]> {
    return (await http.get<MemberTopUp[]>(`${BASE}/member-top-ups`)).data;
  },

  /**
   * Export the key of the master wallet `walletId` (04 §3.4). Without `grant` the server answers 403
   * STEP_UP_REQUIRED with the factors; call again with the grant of purpose MASTER_KEY_EXPORT. The answer carries the
   * key — the caller keeps it only in component state, never logs or stores it.
   */
  async exportKey(walletId: string, grant?: string): Promise<MasterKeyExport> {
    const headers: Record<string, string> = grant ? { 'X-Reveal-Grant': grant } : {};
    return (await http.post<MasterKeyExport>(`${BASE}/${encodeURIComponent(walletId)}/key-export`, null,
      { headers })).data;
  },
};
